import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  inject,
  signal,
} from '@angular/core';
import { MenuModule } from 'primeng/menu';
import { PageContainerService } from '../../services/page-container.service';
import {
  BISettingRoutes,
  settingRoutes,
} from 'src/app/shared/common/route.conatant';
import { Router } from '@angular/router';
import { PermissionsService } from '@core/services/permissions.service';
import { NavigationItem } from '../../interfaces/navigationList.interface';
import { environment } from 'src/environments/environment';
import { tap } from 'rxjs';
import { CommonModule } from '@angular/common';
import { SettingPermissionsService } from '@core/services';
import { BISettingsPermissionsScreenCodes } from '@shared/common/permissionsList.enum';

@Component({
  selector: 'z-multi-settings-menu',
  standalone: true,
  imports: [MenuModule, CommonModule],
  templateUrl: './multi-settings-menu.component.html',
  styleUrl: './multi-settings-menu.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class MultiSettingsMenuComponent {
  readonly #pageContainerService = inject(PageContainerService);
  readonly #router = inject(Router);
  readonly #permissionsService = inject(PermissionsService);
  readonly #settingPermissionsService = inject(SettingPermissionsService);
  readonly #destroyRef = inject(DestroyRef);

  activeSectionRoute$ = this.#pageContainerService.activeSectionRoute.pipe(
    tap(route => {
      if (
        route &&
        (route.id === this.settingRoutes()?.id ||
          route.id === this.BISettingRoutes()?.id)
      ) {
        this.isActive.set(true);
      } else {
        this.isActive.set(false);
      }
    }),
  );
  settingRoutes = signal<NavigationItem>(
    this.filterNavigationList([settingRoutes])[0],
  );
  BISettingRoutes = signal<NavigationItem>(
    this.filterAndUpdateSettingNavigationList([BISettingRoutes])[0],
  );
  isActive = signal(false);

  settingMenuItems = signal([
    {
      label: 'System Settings',
      disabled: false,
      command: () => {
        this.#pageContainerService.activeSectionRoute.next(
          this.settingRoutes(),
        );
        if (this.settingRoutes().isOutSideUrl) {
          const service =
            this.settingRoutes().outsideUrlTarget === 'core'
              ? ''
              : `/${this.settingRoutes().outsideUrlTarget}`;
          const url =
            environment.localDomain + service + this.settingRoutes().routeUrl;
          window.open(url, '_self');
        } else {
          this.#router.navigate([this.settingRoutes().routeUrl]);
        }
      },
    },
  ]);

  ngAfterViewInit() {
    const sub =
      this.#settingPermissionsService.settingScreensPermissions$.subscribe(
        () => {
          if (this.BISettingRoutes().children.length) {
            this.settingMenuItems.set([
              this.settingMenuItems()[0],
              {
                label: 'BI Portal Settings',
                disabled: false,
                command: () => {
                  this.#pageContainerService.activeSectionRoute.next(
                    this.filterNavigationList([this.BISettingRoutes()])[0],
                  );
                  this.#router.navigate([this.BISettingRoutes().routeUrl]);
                },
              },
            ]);
          }
        },
      );
    this.#destroyRef.onDestroy(() => sub.unsubscribe());
  }

  private filterNavigationList(items: NavigationItem[]): NavigationItem[] {
    return items
      .map(item => {
        // Recursively process children
        const filteredChildren = this.filterNavigationList(item.children || []);

        // Check if the current item has permissions
        const parentHasPermission =
          item.permissions &&
          this.#permissionsService.hasAnyPermission(item.permissions);

        // Retain item if:
        // - It has its own permissions
        // - It has children with valid permissions (filteredChildren is not empty)
        if (parentHasPermission || filteredChildren.length > 0) {
          return {
            ...item,
            children: filteredChildren, // Include only filtered children
          };
        }

        // Exclude items without permissions or valid children
        return null;
      })
      .filter(item => item !== null); // Remove null items
  }

  private filterAndUpdateSettingNavigationList(
    items: NavigationItem[],
  ): NavigationItem[] {
    return items
      .map(item => {
        // Recursively process children
        const filteredChildren = this.filterAndUpdateSettingNavigationList(
          item.children || [],
        );

        this.updateSettingRoute(item);

        // Check if the current item has setting permissions
        const parentHasSettingPermission =
          item.settingPermissions &&
          this.#settingPermissionsService.hasAnyPermission(
            item.settingPermissions,
          );

        // Retain item if:
        // - It has its own setting permissions
        // - It has children with valid setting permissions (filteredChildren is not empty)
        if (parentHasSettingPermission || filteredChildren.length > 0) {
          return {
            ...item,
            children: filteredChildren, // Include only filtered children
          };
        }

        // Exclude items without setting permissions or valid children
        return null;
      })
      .filter(item => item !== null); // Remove null items
  }

  updateSettingRoute(item: NavigationItem) {
    const settingScreensData =
      this.#settingPermissionsService.settingScreensPermissions();

    if (item.settingPermissions?.length === 1) {
      const currentSetting = settingScreensData.find(
        x =>
          x.screenCode ===
          BISettingsPermissionsScreenCodes[
            (item as any)
              .settingPermissions[0] as unknown as keyof typeof BISettingsPermissionsScreenCodes
          ],
      );

      item.name = currentSetting?.title ?? item.name;
    }
  }
}
